// SchemaReadyBanner — surfaces unacknowledged schema_ready notifications
// on the source-detail page. Reads /api/v1/sources/:id/notifications,
// renders one row per (event_type, count) pair with an inline
// "Connect to BigQuery" button that fans out per-event_type external
// tables + views via /auto-connect-bq.
//
// Cost preview: as soon as a notification appears, we kick off a
// dry-run /auto-connect-bq?dry_run=true call to compute total bytes +
// per-scan cost *before* the operator clicks Connect. The hint and
// per-scan price are shown inline so the operator commits with eyes
// open. Symmetrical: the post-connect response carries the same shape,
// so the banner UX is consistent before vs after.
//
// Each banner row clears itself on Acknowledge or after the connect
// completes. Refetches every 30s while the page is mounted so a new
// schema crossing fires the banner without a page reload.
//
// BACKLOG: "Schema-detected event publisher" + "auto-connect-bq" +
// "Cost preview component" under Minimum-action onboarding.

import React, { useCallback, useEffect, useState } from 'react';
import {
  listNotifications,
  acknowledgeNotificationApi,
  autoConnectBq,
  getEventsSchemaByEventType,
  type SchemaNotification,
  type AutoConnectBqView,
  type SchemaResponseGroupedBucket,
} from '../api';
import { useToast } from './Toast';

const REFRESH_INTERVAL_MS = 30_000;

// Sub-shape of dry-run /auto-connect-bq response. Same cost_estimate
// block as the post-connect response, just without bq_dataset/view IDs.
interface DryRunEstimate {
  event_type: string;
  count: number;
  object_count?: number;
  cost_estimate?: AutoConnectBqView['cost_estimate'];
}

function fmtUsd(n: number | undefined): string {
  if (n === undefined || !Number.isFinite(n)) return '—';
  if (n >= 0.01) return `$${n.toFixed(2)}`;
  if (n > 0) return `< $0.01`;
  return '$0.00';
}

export function SchemaReadyBanner({ sourceId }: { sourceId: string }): React.ReactElement | null {
  const toast = useToast();
  const [notifs, setNotifs] = useState<SchemaNotification[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [connectedViews, setConnectedViews] = useState<Record<string, AutoConnectBqView>>({});
  const [estimates, setEstimates] = useState<Record<string, DryRunEstimate>>({});
  // event_type → bucket (cached). Populated lazily after the first poll
  // returns notifications. We only fetch once per source-mount because
  // the schema buckets don't change between polls.
  const [schemaByType, setSchemaByType] = useState<Record<string, SchemaResponseGroupedBucket>>({});

  const reload = useCallback(() => {
    listNotifications(sourceId, false)
      .then(setNotifs)
      .catch(() => undefined); // silent — banner is informational
  }, [sourceId]);

  useEffect(() => {
    reload();
    const handle = window.setInterval(reload, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [reload]);

  // Fetch a dry-run cost estimate for each new notification — best-effort.
  // De-duped via the estimates map keyed by notification_id so we don't
  // refetch on every poll.
  useEffect(() => {
    for (const n of notifs) {
      if (estimates[n.notification_id] !== undefined) continue;
      if (connectedViews[n.notification_id] !== undefined) continue;
      let cancelled = false;
      autoConnectBq(sourceId, { event_types: [n.event_type], min_events_threshold: 1, dry_run: true })
        .then(result => {
          if (cancelled) return;
          const view = (result.views ?? []).find(v => v.event_type === n.event_type) as DryRunEstimate | undefined;
          if (view) setEstimates(prev => ({ ...prev, [n.notification_id]: view }));
        })
        .catch(() => undefined); // silent — UI degrades to count-only label
      return () => { cancelled = true; };
    }
  }, [notifs, estimates, connectedViews, sourceId]);

  // Fetch the per-event-type schema map once when notifications first
  // appear. Each bucket has the inferred field list — we surface the
  // field count + first 3 field names so operators see what they're
  // about to materialise.
  useEffect(() => {
    if (notifs.length === 0) return;
    if (Object.keys(schemaByType).length > 0) return;
    let cancelled = false;
    getEventsSchemaByEventType(sourceId)
      .then(r => {
        if (cancelled) return;
        const map: Record<string, SchemaResponseGroupedBucket> = {};
        for (const b of r.schemas) map[b.event_type] = b;
        setSchemaByType(map);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [notifs, schemaByType, sourceId]);

  if (notifs.length === 0) return null;

  async function handleConnect(n: SchemaNotification): Promise<void> {
    setBusy(n.notification_id);
    try {
      const result = await autoConnectBq(sourceId, {
        event_types: [n.event_type],
        // Keep threshold low — the banner already proved the event_type
        // exists with at least n.count events.
        min_events_threshold: 1,
      });
      const view = result.views.find(v => v.event_type === n.event_type);
      if (view) {
        setConnectedViews(prev => ({ ...prev, [n.notification_id]: view }));
        toast.show(`Connected "${n.event_type}" → ${view.fully_qualified_view}`, { kind: 'success' });
      } else {
        const skipReason = result.skipped.find(s => s.event_type === n.event_type)?.reason;
        toast.show(`Connect skipped: ${skipReason ?? 'no view returned'}`, { kind: 'warning' });
      }
      await acknowledgeNotificationApi(sourceId, n.notification_id);
      reload();
    } catch (e) {
      toast.show(`Connect failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }

  async function handleDismiss(n: SchemaNotification): Promise<void> {
    setBusy(n.notification_id);
    try {
      await acknowledgeNotificationApi(sourceId, n.notification_id);
      reload();
    } catch (e) {
      toast.show(`Dismiss failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
      {notifs.map(n => {
        const connected = connectedViews[n.notification_id];
        const estimate = estimates[n.notification_id];
        const schema = schemaByType[n.event_type];
        const perScan = estimate?.cost_estimate?.t2_query_cost_usd_per_full_scan;
        const fieldNames = (schema?.fields ?? []).slice(0, 3).map(f => f.path);
        const hasMoreFields = (schema?.fields.length ?? 0) > 3;
        return (
          <div
            key={n.notification_id}
            role="status"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '10px 14px',
              background: 'oklch(0.96 0.06 150 / 0.6)',
              border: '1px solid oklch(0.78 0.10 150)',
              borderRadius: 8,
              fontSize: 12.5,
              color: 'var(--ink)',
            }}
          >
            <span aria-hidden style={{ fontSize: 14 }}>📊</span>
            <strong>{n.event_type}</strong>
            <span style={{ color: 'var(--ink-2)' }}>
              · {n.count} event{n.count === 1 ? '' : 's'} captured
              {schema && (
                <> · <span title={schema.fields.map(f => `${f.path} (${f.types.join('|')})`).join('\n')}>
                  {schema.fields.length} field{schema.fields.length === 1 ? '' : 's'}
                </span></>
              )}
              {!connected && ' — schema is ready for BigQuery.'}
            </span>
            {!connected && fieldNames.length > 0 && (
              <span
                style={{
                  fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)',
                  padding: '2px 6px',
                  background: 'oklch(0.98 0 0 / 0.5)',
                  border: '1px solid oklch(0.85 0.05 150)',
                  borderRadius: 4,
                  maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
                title={schema?.fields.map(f => `${f.path} (${f.types.join('|')})`).join('\n')}
              >
                {fieldNames.join(', ')}{hasMoreFields ? ` +${schema!.fields.length - 3}` : ''}
              </span>
            )}
            {!connected && estimate && (
              <span
                title={
                  estimate.cost_estimate
                    ? `${estimate.object_count ?? 0} GCS objects · avg ${estimate.cost_estimate.avg_bytes_per_event} B/event · ${estimate.cost_estimate.hint}`
                    : 'cost estimate unavailable'
                }
                style={{
                  fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)',
                  padding: '2px 6px',
                  background: 'oklch(0.98 0 0 / 0.5)',
                  border: '1px solid oklch(0.85 0.05 150)',
                  borderRadius: 4,
                }}
              >≈ {fmtUsd(perScan)} / full scan</span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              {connected ? (
                <>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>connected</span>
                  {connected.cost_estimate?.hint && (
                    <span
                      style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', maxWidth: 360 }}
                      title={`avg ${connected.cost_estimate.avg_bytes_per_event} B/event · ~$${connected.cost_estimate.t2_query_cost_usd_per_full_scan.toFixed(4)} per full scan`}
                    >{connected.cost_estimate.hint}</span>
                  )}
                  {connected.links.bq && (
                    <a
                      href={connected.links.bq}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn sm ghost"
                      style={{ textDecoration: 'none' }}
                    >open in BQ ↗</a>
                  )}
                  {connected.links.sheets && (
                    <a
                      href={connected.links.sheets}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn sm ghost"
                      style={{ textDecoration: 'none' }}
                    >open in Sheets ↗</a>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => { void handleConnect(n); }}
                    disabled={busy !== null}
                    title={`Create a BigQuery external table + view for ${n.event_type} events`}
                  >{busy === n.notification_id ? 'Connecting…' : 'Connect to BigQuery'}</button>
                  <button
                    type="button"
                    className="btn sm ghost"
                    onClick={() => { void handleDismiss(n); }}
                    disabled={busy !== null}
                    title="Dismiss without connecting"
                  >dismiss</button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
